import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import {
  DeleteOutlined as DeleteOutlinedIcon,
  Add as AddIcon
} from '@material-ui/icons';
import { State, WorkflowInvoke } from '@sierralabs/workflow';

import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin
} from '../ServicePlugin';

import {
  createJiraIssueFields,
  getJiraIssueEntityFieldOptions,
  useJiraConnectAPI,
  useUpdateJiraIssues
} from '../../../jira-connect/JiraConnect.hooks';
import {
  TaskContext,
  UpdateJiraIssueParams
} from '../../../tasks/Task.machine';
import { Alert, AlertTitle } from '@material-ui/lab';
import JiraIssueTypePicker from '../../../jira-connect/pickers/JiraIssueTypePicker';
import JiraIssueFieldPicker from '../../../jira-connect/pickers/JiraIssueFieldPicker';
import JiraProjectPicker from '../../../jira-connect/pickers/JiraProjectPicker';
import EntityColumnSelector from '../../../entity/components/EntityColumnSelector';
import { useEntityContext } from '../../../entity/Entity.context';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .MuiTableCell-root {
    padding: 12px 5px 12px 5px;
  }
`;

const UpdateJiraIssueConfigurator: React.FC<ServiceConfiguratorProps> = ({
  targetEntity,
  onChange,
  ...props
}) => {
  const [invoke, setInvoke] = useState(props.invoke);
  const { schemas } = useEntityContext();
  if (!invoke.data) invoke.data = {};
  const data: UpdateJiraIssueParams['data'] = invoke.data;

  const options = useMemo(() => {
    const entity = targetEntity
      ? schemas.find(schema => schema.name === targetEntity)
      : undefined;
    return getJiraIssueEntityFieldOptions({ entities: schemas, entity });
  }, [schemas, targetEntity]);

  const handleUpdateInvoke = (data: UpdateJiraIssueParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  return (
    <StyledConfiguratorDiv>
      <JiraProjectPicker
        value={data.projectId}
        textFieldProps={{ size: 'small' }}
        onChange={projectId => {
          projectId && handleUpdateInvoke({ projectId: projectId, fields: [] });
        }}
      />
      {data.projectId && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Entity Field</TableCell>
                <TableCell>Issue Type</TableCell>
                <TableCell>Issue Field</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.fields?.length > 0 &&
                data.fields.map((item, index) => (
                  <TableRow>
                    <TableCell>
                      <EntityColumnSelector
                        options={options}
                        value={item.entityField}
                        textFieldProps={{
                          size: 'small',
                          InputLabelProps: { style: { fontSize: 10 } }
                        }}
                        onChange={propertyName => {
                          // NOTE: passed in options prop uses property name instead of columnName
                          data.fields[index].entityField = propertyName || '';
                          handleUpdateInvoke({ ...data, fields: data.fields });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <JiraIssueTypePicker
                        value={item.issueType?.id}
                        disabled={!item.entityField}
                        textFieldProps={{
                          size: 'small',
                          InputLabelProps: { style: { fontSize: 10 } }
                        }}
                        onChange={issueType => {
                          data.fields[index].issueType = issueType && {
                            id: issueType.id,
                            name: issueType.label
                          };
                          handleUpdateInvoke({ ...data, fields: data.fields });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <JiraIssueFieldPicker
                        issueTypeId={item.issueType?.id || ''}
                        projectId={data.projectId}
                        value={item.issueField}
                        disabled={!item.issueType?.id}
                        textFieldProps={{
                          size: 'small',
                          InputLabelProps: { style: { fontSize: 10 } }
                        }}
                        onChange={issueField => {
                          data.fields[index].issueField = issueField;
                          handleUpdateInvoke({ ...data, fields: data.fields });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size='small'
                        onClick={() => {
                          data.fields.splice(index, 1);
                          handleUpdateInvoke({ ...data, fields: data.fields });
                        }}
                      >
                        <DeleteOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              <TableRow>
                <TableCell colSpan={9}>
                  <Button
                    color='primary'
                    variant='outlined'
                    startIcon={<AddIcon />}
                    onClick={() => {
                      data.fields.push({ entityField: '', issueField: '' });
                      handleUpdateInvoke({ ...data, fields: data.fields });
                    }}
                  >
                    Add Item
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </StyledConfiguratorDiv>
  );
};

const UpdateJiraIssueDisplay: React.FC<ServiceDisplayProps> = ({ invoke }) => {
  return null;
};

const StyledDialogContent = styled(DialogContent)`
  .MuiTypography-root,
  .MuiAlert-root {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

export interface UpdateJiraIssueDialogProps {
  open: boolean;
  data: UpdateJiraIssueParams['data'];
  state: State<TaskContext>;
  autoSkip?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const UpdateJiraIssueDialog: React.FC<UpdateJiraIssueDialogProps> = props => {
  const { open, data, state, autoSkip, onCancel, onSuccess } = props;
  const stateRef = useRef(state);
  stateRef.current = state;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const [alertMessage, setAlertMessage] = useState<React.ReactNode>();
  const { isAvailable } = useJiraConnectAPI();
  const [updateResult, updateIssues] = useUpdateJiraIssues();
  const alertMsg = !isAvailable
    ? 'Unable to run action and update Jira issue(s). ' +
      'You can choose to skip this action, or open SierraQMS ' +
      'within JIRA and try again to properly run this action.'
    : alertMessage;

  // run update once component is rendered
  useEffect(() => {
    const dataFields = data.fields || [];
    if (!isAvailable || dataFields.length === 0) {
      return;
    }

    const { task, taskEntity } = stateRef.current.context;
    const jiraIssues = task.jiraIssues;
    if (!taskEntity || !jiraIssues || jiraIssues.length === 0) {
      const selectedIssueTypes = dataFields
        .map(item => `"${item.issueType?.name}"`)
        .join(', ');
      setAlertMessage(
        !taskEntity
          ? 'Unable to load the Task entity. You can choose to skip this action.'
          : 'There are no linked JIRA issues that match some of the ' +
              `issue type(s): ${selectedIssueTypes}, please link an issue that ` +
              'matches the given issue type(s) to proceed. You can choose to skip this action.'
      );
      return;
    }

    setAlertMessage(undefined);
    const filteredJiraIssues = jiraIssues.filter(jiraIssue => {
      return dataFields.some(
        item => item.issueType?.id === jiraIssue.issue.fields.issuetype.id
      );
    });
    const updates = filteredJiraIssues.map(issue => {
      const allFields = dataFields.filter(
        field => field.issueType?.id === issue.issue.fields.issuetype.id
      );
      const fields = createJiraIssueFields(taskEntity, allFields);
      return { issueId: issue.issue.id, dto: { fields } };
    });
    updateIssues(updates);
  }, [data, isAvailable, updateIssues]);

  useEffect(() => {
    if (updateResult.success) {
      onSuccessRef.current?.();
    }
  }, [updateResult.success]);

  // auto skip if alert message is set
  useEffect(() => {
    if (alertMsg && autoSkip) {
      onSuccessRef.current?.();
    }
  }, [alertMsg, autoSkip]);

  return (
    <Dialog open={open} onClose={onCancel}>
      {updateResult.isLoading && <LinearProgress />}
      <DialogTitle>Update Jira Issue</DialogTitle>
      <StyledDialogContent>
        {updateResult.isLoading && (
          <Typography>Updating Jira Issue(s)...</Typography>
        )}
        {alertMsg && (
          <Alert severity='warning'>
            <AlertTitle>
              {!isAvailable
                ? 'JIRA Connect API is not available'
                : 'Unable to update JIRA issue(s)'}
            </AlertTitle>
            {alertMsg}
          </Alert>
        )}
        {updateResult.errors.length > 0 && (
          <Alert severity='error'>
            {updateResult.errors.map((error, index) => (
              <div key={index}>{error.message}</div>
            ))}
          </Alert>
        )}
      </StyledDialogContent>
      <DialogActions>
        <Button disabled={updateResult.isLoading} onClick={onCancel}>
          Cancel
        </Button>
        {alertMsg && (
          <Button
            color='primary'
            variant='outlined'
            disabled={updateResult.isLoading}
            onClick={() => onSuccess?.()}
          >
            Skip Action and Proceed
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const UpdateJiraIssuePlugin: ServicePlugin<UpdateJiraIssueParams> = {
  src: 'updateJiraIssue',
  name: 'Update Jira Issue',
  configurator: UpdateJiraIssueConfigurator,
  display: UpdateJiraIssueDisplay,
  validate: (condition: Partial<WorkflowInvoke>) => {
    const data = condition.data as UpdateJiraIssueParams['data'];
    return !!(
      data &&
      data.projectId &&
      Array.isArray(data.fields) &&
      data.fields.every(
        field => field.entityField && field.issueField && field.issueType
      )
    );
  },
  service: (state: State<TaskContext>, params, render) => {
    console.log('updateJiraIssue params', state, params);
    if (
      state.isSimulation ||
      state.context.skipServicePlugins?.includes(UpdateJiraIssuePlugin.src)
    ) {
      return true;
    }
    const pluginParams = Array.isArray(params) ? params[0] : params;
    return new Promise((resolve, reject) => {
      render?.(
        <UpdateJiraIssueDialog
          open
          data={pluginParams.data}
          state={state}
          autoSkip={state.context.autoSkipJiraPlugins?.includes(
            UpdateJiraIssuePlugin.src
          )}
          onSuccess={() => {
            render?.(undefined);
            resolve(true);
          }}
          onCancel={() => {
            render?.(undefined);
            reject(true);
          }}
        />
      );
    });
  }
};
